import React from 'react';
import { Skeleton, Box, Card, CardContent, Stack } from '@mui/material';

interface SkeletonLoaderProps {
  variant?: 'card' | 'list' | 'table' | 'form' | 'custom';
  count?: number;
  height?: number | string;
  width?: number | string;
  children?: React.ReactNode;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant = 'card',
  count = 1,
  height,
  width,
  children,
}) => {
  if (variant === 'custom' && children) {
    return <>{children}</>;
  }

  const renderSkeleton = () => {
    switch (variant) {
      case 'card':
        return (
          <Card sx={{ width: width || '100%', height: height || 'auto' }}>
            <CardContent>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={100} sx={{ mt: 2 }} />
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        );

      case 'list':
        return (
          <Box sx={{ width: width || '100%' }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: 'center',
                p: 2
              }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" height={24} />
                <Skeleton variant="text" width="50%" height={20} />
              </Box>
              <Skeleton variant="rectangular" width={80} height={32} />
            </Stack>
          </Box>
        );

      case 'table':
        return (
          <Box sx={{ width: width || '100%' }}>
            <Skeleton variant="rectangular" width="100%" height={56} />
            {Array.from({ length: 5 }).map((_, index) => (
              <Stack
                key={index}
                direction="row"
                spacing={2}
                sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Skeleton variant="text" width="20%" />
                <Skeleton variant="text" width="30%" />
                <Skeleton variant="text" width="25%" />
                <Skeleton variant="text" width="25%" />
              </Stack>
            ))}
          </Box>
        );

      case 'form':
        return (
          <Box sx={{ width: width || '100%', p: 2 }}>
            <Skeleton variant="text" width="30%" height={32} sx={{ mb: 3 }} />
            {Array.from({ length: 4 }).map((_, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" width="100%" height={56} />
              </Box>
            ))}
            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Skeleton variant="rectangular" width={120} height={40} />
              <Skeleton variant="rectangular" width={120} height={40} />
            </Stack>
          </Box>
        );

      default:
        return <Skeleton variant="rectangular" width={width} height={height} />;
    }
  };

  return (
    <Stack spacing={2}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index}>{renderSkeleton()}</Box>
      ))}
    </Stack>
  );
};
